import LaunchIcon from "@mui/icons-material/Launch";
import { Button, colors, Grid, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ChatMessage } from "chat/types";
import { ReactComponent as FileIcon } from "components/doc_icon.svg";
import formatDistanceToNow from "date-fns/format";
import { useWindowTilesContext } from "desktopWindow/context/WindowTilesContext";
import { getImageSize } from "fileBrowser/helpers/fileBrowserHelpers";

const useMessageStyles = makeStyles({
	root: {
		padding: "5px 24px",
		"&:hover": {
			background: colors.grey["200"],
		},
		display: "flex",
		flexDirection: "column",
	},
	header: {
		display: "inline-block",
	},
	username: {
		fontWeight: "bold",
		marginRight: 4,
	},
	time: {
		fontWeight: 200,
		fontSize: 11,
	},
	text: {
		wordWrap: "break-word",
		whiteSpace: "pre-line",
		display: "inline-block",
		"& p": {
			margin: 0,
		},
	},
});

function getUserColorSettingsByName(userColorSettings: any, name: string, users: any) {
	const user = users.find((user) => user.name === name);
	const userId = user?._id;
	return userColorSettings.find((setting) => setting.userId === userId)?.color;
}

type Props = {
	message: ChatMessage;
	userColorSettings: any;
	users: any;
};

export const Message = (props: Props) => {
	const classes = useMessageStyles();
	const { message, userColorSettings, users } = props;

	const color = getUserColorSettingsByName(userColorSettings, message.from, users);

	const { openWindow } = useWindowTilesContext();
	const onClickAttachment = async ({ name, link }) => {
		const isImage = link.includes("jpg") || link.includes("png");
		const { width, height } = isImage
			? await getImageSize(link).then((value) => {
					return {
						width: Math.max(500, value.width),
						height: Math.max(500, value.height),
					};
			  })
			: { width: 800, height: 600 };

		openWindow({
			id: link,
			componentName: "FileViewer",
			componentProps: { file: { fileUrl: link, name: name } },
			windowTitle: name,
			size: { height, width },
			minSize: { height: height * 0.5, width: width * 0.5 },
		});
	};

	return (
		<div className={classes.root}>
			<div className={classes.header}>
				<span className={classes.time}>{formatDistanceToNow(new Date(message.timestamp), "hh:mm aa")}</span>
			</div>
			<div>
				<span className={classes.username} style={{ color }}>
					{message.from}
				</span>
				<Typography className={classes.text} component="span">
					{message.text}
				</Typography>
				{message.attachments ? (
					<Grid
						item
						sm={12}
						md={12}
						lg={12}
						container
						direction="row"
						alignItems="center"
						justifyContent="flex-end"
					>
						{message.attachments.map(({ name, link }) => {
							const isImage: boolean | undefined = link?.includes("jpg") || link?.includes("png");
							return (
								<Grid key={name} item sm={12} md={12} style={{ padding: "10px 0px" }}>
									<Button
										variant="outlined"
										onClick={() => onClickAttachment({ name, link })}
										style={{
											textTransform: "none",
											backgroundColor: "#FAFAFA",
											borderColor: "#FAFAFA",
											fontSize: 16,
										}}
										startIcon={
											isImage ? (
												<img
													style={{ width: "100%", maxHeight: 25, maxWidth: 25 }}
													src={link}
													alt={link}
												/>
											) : (
												<FileIcon style={{ maxHeight: 25, maxWidth: 25 }} />
											)
										}
										endIcon={<LaunchIcon />}
									>
										{name}
									</Button>
								</Grid>
							);
						})}
					</Grid>
				) : (
					<></>
				)}
			</div>
		</div>
	);
};
